import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { NgxSmartModalService } from 'ngx-smart-modal';
import {
  StepSurveyResponseCategory,
  StepSurveyOptionsLayout,
  StepPopupActions,
  StepElements,
  CustomStepCategoryEnum,
  FunnelInputTypeEnum,
  FunnelInput,
  FunnelType,
  Funnel,
  User,
  Campaign,
  AudiencePopup,
  OfferIntents,
  OfferTypes,
  StageSurveyMappings,
  FunnelFulfillmentType,
  SessionSourceLabels,
  AudienceCondition,
} from '../../../_models';

import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import {
  CancelReasonCategoryService,
  StepEmulatorService,
  FunnelService,
  CampaignService,
  AudienceService,
  AlertService,
  CampaignProductService,
} from '../../../_services';
import { expandCollapse, fadeInOut } from '../../../_shared/animations/app-animations';
import { Form, FormControlStatus } from '../../../_forms';
import { StepHints, TroubleshooterOfferIntents } from '../../step-util';
import { FunnelInputFieldsComponent } from '../../../funnel-input';
import { config } from '../../../../config/config';
import { PopupData, RetentionOffer } from '../../../offer-builder/retention/retention';
import { forkJoin } from 'rxjs';

@Component({
  selector: 'step-survey-options',
  templateUrl: './step-survey-options.component.html',
  styleUrls: [
    './step-survey-options.component.scss',
    '../../../offer-builder/retention/retention-offers.component.scss',
  ],
  animations: [fadeInOut, expandCollapse],
})
export class StepSurveyOptionsComponent extends Form implements OnInit, OnChanges {
  @Input() layout: StepSurveyOptionsLayout;
  @Input() form: FormGroup;
  @Input() enableFormErrors: boolean;
  @Input() templateId: string;
  @Input() stepId: string;
  @Input() stepType: CustomStepCategoryEnum;
  @Input() user: User;
  @Input() offerIntent: OfferIntents;
  @Input() offerType: OfferTypes;
  @Input() retentionOffers: RetentionOffer[] = [];
  funnel: Funnel;
  campaign: Campaign;
  offers: RetentionOffer[] = [];
  filteredOffers: RetentionOffer[] = [];
  selectedOffers: { [key: number]: string } = {};
  openDropdownIndex: number | null = null;
  searchQuery: { [key: number]: string } = {};
  offerLoading: { [key: number]: boolean } = {};
  audiencePopup = AudiencePopup;
  stepElements = StepElements;
  selectedResponse: Partial<StepSurveyResponseCategory> = {};
  isEditing: boolean;
  editIndex: number;
  addingNewResponse: boolean = false;
  newResponse: string = '';
  surveyOptionsLoading: boolean = true;
  categoriesLoading: boolean = false;
  addingCategory: boolean = false;
  cancelReasonCategories: any[] = [];
  popupTitle = '';
  stepHints = StepHints;
  popupData = {
    category_id: null,
    is_category: true,
    popupType: StepPopupActions.Delete,
  };
  segmentPopupTitle = '';
  segmentPopupData: PopupData = {};
  currentSegmentInputIndex: number | null = null;
  conditionsPopoverIndex: number | null = null;
  dropdownHoveredOfferKey: string | null = null;
  private dropdownHideTimer: any = null;
  audienceProductCounts: { [audienceId: number]: number } = {};
  public selectedInput: FunnelInput = null;
  public nextStep: number = null;
  isAdminView: boolean = false;
  selectedInputIndex: number;
  showThreeDotMenu: { [key: number]: boolean } = {};
  @ViewChild(FunnelInputFieldsComponent, { static: false })
  inputComponent: FunnelInputFieldsComponent;

  constructor(
    public ngxSmartModalService: NgxSmartModalService,
    protected cancelReasonCategoriesService: CancelReasonCategoryService,
    protected stepEmulatorService: StepEmulatorService,
    protected formBuilder: FormBuilder,
    protected route: ActivatedRoute,
    protected funnelService: FunnelService,
    protected campaignService: CampaignService,
    protected audienceService: AudienceService,
    protected alertService: AlertService,
    protected campaignProductService: CampaignProductService,
    protected router: Router,
    protected location: Location
  ) {
    super(alertService, router, location);
  }

  ngOnInit() {
    this.isAdminView = this.route.snapshot.data['isAdminView'];
    this.campaign = this.campaignService.getCurrentCampaign();
    this.setNextStep();
    this.getSurveyOptions();
    this.funnel = {
      resourcetype: FunnelType.Product,
      is_visual: true,
      offer_intent: this.form.get('offer_intent').value,
      is_voice: false,
      is_sms: false,
    } as Funnel;

    if (this.retentionOffers && this.retentionOffers.length > 0) {
      this.applyRetentionOffers(this.retentionOffers);
    }

  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['retentionOffers'] && changes['retentionOffers'].currentValue && changes['retentionOffers'].currentValue.length > 0) {
      this.applyRetentionOffers(changes['retentionOffers'].currentValue);
    }
  }

  applyRetentionOffers(offers: RetentionOffer[]): void {
    this.offers = offers;
    this.filteredOffers = [...offers];
    this.mapInputsToOffers();
    this.fetchAudienceProducts();
    if (!this.isElementVisible(StepElements.SurveyOptions)) {
      this.toggleElement(StepElements.SurveyOptions);
    }
  }

  getSurveyOptions(): void {
    this.surveyOptionsLoading = true;
    this.cancelReasonCategoriesService.list({ page: 1, page_size: config.maxPageSize }).subscribe(
      (categories: any) => {
        this.surveyOptionsLoading = false;
        this.cancelReasonCategories = categories.results;
        const categoryMap = {};
        categories.results.forEach((category: any) => {
          categoryMap[category.name] = category.id;
        });

        if (!this.templateId && !this.stepId) {
          let items = this.layout.items;
          if (Object.keys(StageSurveyMappings).includes(this.offerIntent.toString())) {
            items = StageSurveyMappings[this.offerIntent];
          }

          const formGroups = items
            .map((item) => {
              const categoryId = categoryMap[item.category];
              if (categoryId) {
                return this.formBuilder.group({
                  label: [item.reason, Validators.required],
                  help: null,
                  next_step: null,
                  type: FunnelInputTypeEnum.Radio,
                  values: [[]],
                  actions: [[]],
                  icon: null,
                  hide_if_invalid: true,
                  product_funnels: [[]],
                  classes: [[]],
                  is_alt_child_exit: false,
                  matched_step_key: null,
                  autopick_next_step: null,
                  reason_category: [categoryId, Validators.required],
                  troubleshooter_enabled: false,
                });
              }
              return null;
            })
            .filter((group) => group !== null);

          this.form.setControl('inputs', this.formBuilder.array(formGroups, Validators.required));
        }
      },
      (error) => {
        this.surveyOptionsLoading = false;
        this.handleError(error);
      }
    );
  }

  getCancelReasonCategories(): void {
    this.categoriesLoading = true;
    this.cancelReasonCategoriesService.list({ page: 1, page_size: config.maxPageSize }).subscribe(
      (categories: any) => {
        this.categoriesLoading = false;
        this.cancelReasonCategories = categories.results;
      },
      (error) => {
        this.categoriesLoading = false;
        this.handleError(error);
      }
    );
  }

  getCancelCategoryName(input: FormGroup): string {
    const reason_category = input.get('reason_category');

    if (reason_category) {
      const category = this.cancelReasonCategories.find((x) => x.id === reason_category.value);
      return category ? category.name : null;
    } else return null;
  }

  cancelAddingNewResponse() {
    this.newResponse = '';
    this.addingNewResponse = false;
  }

  addNewOption(newResponse: string) {
    if (newResponse.trim() !== '') {
      this.addingCategory = true;
      const newCategoryData = {
        name: newResponse.trim(),
        is_global: false,
      };

      this.cancelReasonCategoriesService.create(newCategoryData).subscribe(
        () => {
          this.addingCategory = false;
          this.getCancelReasonCategories();
        },
        (error) => {
          this.addingCategory = false;
          this.handleError(error);
        }
      );

      this.newResponse = '';
    }
  }

  drop(event: CdkDragDrop<any[]>): void {
    // Close any open dropdown
    this.closeDropdown();

    const inputsArray = this.form.get('inputs') as FormArray;

    // Rearrange items in the FormArray
    const movedItem = inputsArray.at(event.previousIndex);
    inputsArray.removeAt(event.previousIndex);
    inputsArray.insert(event.currentIndex, movedItem);

    // Also update the selectedOffers mapping to match the new order
    const tempOffers = { ...this.selectedOffers };
    const newOffers: { [key: number]: string } = {};

    // Create a mapping array to track the new positions
    const itemsOrder: number[] = [];
    for (let i = 0; i < inputsArray.length; i++) {
      itemsOrder.push(i);
    }

    // Apply the move operation to the order array
    moveItemInArray(itemsOrder, event.previousIndex, event.currentIndex);

    // Rebuild the selectedOffers with new indices
    itemsOrder.forEach((oldIndex, newIndex) => {
      if (tempOffers[oldIndex] !== undefined) {
        newOffers[newIndex] = tempOffers[oldIndex];
      }
    });

    this.selectedOffers = newOffers;
  }

  editResponse(index: number) {
    this.isEditing = true;
    this.editIndex = index;

    const inputs = this.form.get('inputs') as FormArray;

    const troubleshooterControl = inputs.at(index).get('troubleshooter_enabled');

    this.selectedResponse = {
      reason: inputs.at(index).get('label').value,
      categoryId: inputs.at(index).get('reason_category').value,
      troubleshooterEnabled: troubleshooterControl ? troubleshooterControl.value : false,
    };
    this.ngxSmartModalService.getModal('popup').open();
  }

  saveResponse() {
    if (!this.selectedResponse.categoryId || !this.selectedResponse.reason) {
      return;
    }

    const categoryObject = this.cancelReasonCategories.find(
      (category) => category.id === this.selectedResponse.categoryId
    );

    if (this.isEditing) {
      const inputs = this.form.get('inputs') as FormArray;

      inputs.at(this.editIndex).patchValue({
        label: this.selectedResponse.reason,
        reason_category: this.selectedResponse.categoryId,
        troubleshooter_enabled: !!this.selectedResponse.troubleshooterEnabled,
      });
    } else if (categoryObject) {
      const response: StepSurveyResponseCategory = {
        id: this.selectedResponse.id || null,
        reason: this.selectedResponse.reason,
        category: categoryObject.name,
        categoryId: categoryObject.id,
      };

      const inputsArray = this.form.get('inputs') as FormArray;
      const formGroup = this.formBuilder.group({
        label: [response.reason, Validators.required],
        help: null,
        next_step: this.isAdmin ? null : this.nextStep,
        type: FunnelInputTypeEnum.Radio,
        values: [[]],
        actions: [[]],
        icon: null,
        hide_if_invalid: true,
        product_funnels: [[]],
        classes: [[]],
        is_alt_child_exit: false,
        matched_step_key: null,
        autopick_next_step: null,
        reason_category: [response.categoryId, Validators.required],
        troubleshooter_enabled: [!!this.selectedResponse.troubleshooterEnabled],
      });

      inputsArray.push(formGroup);

      // Map the newly added input to an appropriate offer
      if (this.offers.length > 0) {
        this.mapInputsToOffers();
      }
    }

    this.selectedResponse = {};
    this.editIndex = null;
    this.isEditing = false;
    this.ngxSmartModalService.getModal('popup').close();
  }

  cancelResponse() {
    this.isEditing = false;
    this.selectedResponse = {};
    this.ngxSmartModalService.getModal('popup').close();
  }

  deleteResponse(index: number) {
    const confirmed = window.confirm('Are you sure you want to delete this response option?');

    if (confirmed) {
      const inputsArray = this.form.get('inputs') as FormArray;

      if (index >= 0 && index < inputsArray.length) {
        inputsArray.removeAt(index);
      }
    }
  }

  deleteCategory(categoryId: string | number): void {
    this.popupData.category_id = categoryId;
    this.popupTitle = 'Delete Category';
    this.popupData.is_category = true;
    this.ngxSmartModalService.getModal('confirmationModal').open();
  }

  confirmDeleteCategory(): void {
    if (this.popupData.category_id) {
      this.getCancelReasonCategories();
      this.ngxSmartModalService.getModal('confirmationModal').close();
    }
  }

  cancelDeleteCategory(): void {
    this.ngxSmartModalService.getModal('confirmationModal').close();
  }

  toggleElement(element: StepElements) {
    this.stepEmulatorService.toggleElement(element);
  }

  isElementVisible(element: StepElements): boolean {
    return this.stepEmulatorService.isElementVisible(element);
  }

  openInputsPopup(input, index) {
    this.selectedInput = input;
    this.selectedInputIndex = index;
    this.ngxSmartModalService.getModal('surveyActions').open();
  }

  onCloseInputDialog() {
    const input = (this.form.get('inputs') as FormArray).at(this.selectedInputIndex) as FormControl;
    const actions = input.get('actions') as FormArray;
    const data = this.inputComponent.getFormData();

    actions.clear();

    data.actions.forEach((action) => {
      actions.push(new FormControl(action));
    });
  }

  setNextStep(): void {
    const inputs = this.form.get('inputs') as FormArray;
    this.nextStep = inputs.length > 0 ? inputs.at(0).get('next_step').value : null;
  }

  get inputControls() {
    return (this.form.get('inputs') as FormArray).controls;
  }

  get isAdmin(): boolean {
    return this.isAdminView && this.user && this.user.is_staff;
  }

  get canEnableTroubleshooter(): boolean {
    // gated on funnel.offer_type === Survey; a null funnel (undefined offer_type) is a survey
    // template, where we gate purely on the step's offer_intent
    const isSurveyContext = this.offerType == null || this.offerType === OfferTypes.Survey;
    return isSurveyContext && TroubleshooterOfferIntents.indexOf(this.offerIntent) > -1;
  }

  get inputsLength(): number {
    const inputs = this.form.get('inputs') as FormArray;
    return inputs ? inputs.length : 0;
  }

  get inputsInvalidity() {
    return this.enableFormErrors && this.form.controls.inputs.status === FormControlStatus.Invalid;
  }

  getOfferKey(offer: RetentionOffer): string {
    return offer.audience ? `${offer.id}_${offer.audience.id}` : `${offer.id}_default`;
  }

  findOfferByKey(key: string): RetentionOffer | null {
    let result = null;
    if (key) {
      result = this.offers.find((o) => this.getOfferKey(o) === key) || null;
    }
    return result;
  }

  onOfferSelect(inputIndex: number, offerKey: string): void {
    const previousOfferKey = this.selectedOffers[inputIndex];

    const inputs = this.form.get('inputs') as FormArray;
    const inputControl = inputs.at(inputIndex);
    const reasonCategoryControl = inputControl && inputControl.get('reason_category');
    const reasonCategory = reasonCategoryControl && reasonCategoryControl.value;

    if (!reasonCategory) {
      this.selectedOffers[inputIndex] = offerKey;
      this.closeDropdown();
    } else {
      // Find previous and new offers
      const previousOffer = this.findOfferByKey(previousOfferKey);
      const newOffer = this.findOfferByKey(offerKey);

      if (newOffer) {
        this.offerLoading[inputIndex] = true;
        this.closeDropdown();
        this.updateAudienceCategories(previousOffer, newOffer, reasonCategory, inputIndex, offerKey);
      }
    }
  }

  updateAudienceCategories(
    previousOffer: RetentionOffer,
    newOffer: RetentionOffer,
    reasonCategory: number,
    inputIndex: number,
    newOfferKey: string
  ): void {
    const hasPreviousAudience = previousOffer && previousOffer.audience && previousOffer.audience.reason_categories;
    const hasNewAudience = newOffer && newOffer.audience && newOffer.audience.reason_categories;
    const successMessage = 'Survey Response Updated ';

    // Case 1: Previous was default (no audience), new has audience
    if (!hasPreviousAudience && hasNewAudience) {
      const updatedCategories = Array.isArray(newOffer.audience.reason_categories)
        ? [...newOffer.audience.reason_categories, reasonCategory]
        : [reasonCategory];

      this.audienceService
        .patch(newOffer.audience.id, { reason_categories: updatedCategories, use_reason_filter: true })
        .subscribe(
          () => {
            this.selectedOffers[inputIndex] = newOfferKey;
            this.offerLoading[inputIndex] = false;
            this.alertService.success(successMessage);
          },
          (error) => {
            this.offerLoading[inputIndex] = false;
            this.handleError(error);
          }
        );
    }
    // Case 2: Both have audiences
    else if (hasPreviousAudience && hasNewAudience) {
      // Remove from previous, add to new
      const previousCategories = previousOffer.audience.reason_categories.filter((cat) => cat !== reasonCategory);
      const newCategories = Array.isArray(newOffer.audience.reason_categories)
        ? [...newOffer.audience.reason_categories, reasonCategory]
        : [reasonCategory];

      forkJoin({
        removePrevious: this.audienceService.patch(previousOffer.audience.id, {
          reason_categories: previousCategories,
          use_reason_filter: previousCategories.length > 0 ? true : false,
        }),
        addToNew: this.audienceService.patch(newOffer.audience.id, {
          reason_categories: newCategories,
          use_reason_filter: true,
        }),
      }).subscribe(
        () => {
          this.selectedOffers[inputIndex] = newOfferKey;
          this.offerLoading[inputIndex] = false;
          this.alertService.success(successMessage);
        },
        (error) => {
          this.offerLoading[inputIndex] = false;
          this.handleError(error);
        }
      );
    }
    // Case 3: Previous had audience, new is default (no audience)
    else if (hasPreviousAudience && !hasNewAudience) {
      const previousCategories = previousOffer.audience.reason_categories.filter((cat) => cat !== reasonCategory);

      this.audienceService
        .patch(previousOffer.audience.id, {
          reason_categories: previousCategories,
          use_reason_filter: previousCategories.length > 0 ? true : false,
        })
        .subscribe(
          () => {
            this.selectedOffers[inputIndex] = newOfferKey;
            this.offerLoading[inputIndex] = false;
            this.alertService.success(successMessage);
          },
          (error) => {
            this.handleError(error);
            this.offerLoading[inputIndex] = false;
          }
        );
    }
    // Case 4: Both are default (no audiences) - just update selection
    else {
      this.selectedOffers[inputIndex] = newOfferKey;
      this.closeDropdown();
    }
  }

  getSelectedOfferObject(inputIndex: number): RetentionOffer | null {
    const offerKey = this.selectedOffers[inputIndex];
    return this.findOfferByKey(offerKey);
  }

  toggleDropdown(inputIndex: number): void {
    if (this.openDropdownIndex === inputIndex) {
      this.closeDropdown();
    } else {
      this.openDropdownIndex = inputIndex;
      this.searchQuery[inputIndex] = '';
      this.filteredOffers = [...this.offers];
    }
  }

  getSelectedOffer(inputIndex: number): string | null {
    return this.selectedOffers[inputIndex] || null;
  }

  closeDropdown(): void {
    this.openDropdownIndex = null;
    this.conditionsPopoverIndex = null;
    this.dropdownHoveredOfferKey = null;
    if (this.dropdownHideTimer) {
      clearTimeout(this.dropdownHideTimer);
      this.dropdownHideTimer = null;
    }
  }

  showConditionsPopover(inputIndex: number): void {
    this.conditionsPopoverIndex = inputIndex;
  }

  hideConditionsPopover(): void {
    this.conditionsPopoverIndex = null;
  }

  showDropdownConditionsPopover(offerKey: string): void {
    if (this.dropdownHideTimer) {
      clearTimeout(this.dropdownHideTimer);
      this.dropdownHideTimer = null;
    }
    this.dropdownHoveredOfferKey = offerKey;
  }

  hideDropdownConditionsPopover(): void {
    this.dropdownHideTimer = setTimeout(() => {
      this.dropdownHoveredOfferKey = null;
      this.dropdownHideTimer = null;
    }, 150);
  }

  private pluralize(count: number, singular: string, plural: string): string {
    return count + ' ' + (count === 1 ? singular : plural);
  }

  private getAudienceConditions(offer: RetentionOffer): AudienceCondition[] {
    const conditions: AudienceCondition[] = [];
    if (offer && offer.audience) {
      const a = offer.audience;

      if (a.use_reason_filter && a.reason_categories && a.reason_categories.length > 0) {
        const count = a.reason_categories.length;
        const value = this.pluralize(count, 'Category', 'Categories');
        conditions.push({ abbr: 'CR', label: 'Survey Categories', value });
      }

      if (a.use_billing_cycle) {
        let value = '';

        if (a.min_billing_cycle && a.max_billing_cycle) {
          value = a.min_billing_cycle + ' - ' + a.max_billing_cycle;
        } else if (a.min_billing_cycle) {
          value = a.min_billing_cycle + '+';
        }

        conditions.push({ abbr: 'BC', label: 'Billing Cycle', value });
      }

      if (a.fulfillment_type) {
        const value = a.fulfillment_type === FunnelFulfillmentType.Shippable ? 'Shippable' : 'Not Shippable';
        conditions.push({ abbr: 'FT', label: 'Fulfillment Type', value });
      }

      const hasTransactionFilters =
        a.is_3ds ||
        (a.merchant_ids && a.merchant_ids.length > 0) ||
        (a.cc_types && a.cc_types.length > 0) ||
        (a.payment_types && a.payment_types.length > 0);

      if (hasTransactionFilters) {
        const parts: string[] = [];

        if (a.is_3ds) {
          parts.push('3DS');
        }

        if (a.merchant_ids && a.merchant_ids.length > 0) {
          parts.push(this.pluralize(a.merchant_ids.length, 'Merchant', 'Merchants'));
        }

        if (a.cc_types && a.cc_types.length > 0) {
          parts.push(this.pluralize(a.cc_types.length, 'Card Type', 'Card Types'));
        }

        if (a.payment_types && a.payment_types.length > 0) {
          parts.push(this.pluralize(a.payment_types.length, 'Payment Type', 'Payment Types'));
        }

        conditions.push({ abbr: 'TF', label: 'Transaction Filters', value: parts.join(', ') });
      }

      if (a.countries && a.countries.length > 0) {
        let value = a.countries.join(', ');

        if (a.states && a.states.length > 0) {
          value += ' · ' + this.pluralize(a.states.length, 'State', 'States');
        }

        conditions.push({ abbr: 'LOC', label: 'Location', value });
      }

      if (a.traffic_sources && a.traffic_sources.length > 0) {
        const value = a.traffic_sources.map((s) => SessionSourceLabels[s] || s.toString()).join(', ');
        conditions.push({ abbr: 'SS', label: 'Session Source', value });
      }

      if (a.use_product_filter) {
        const count = this.audienceProductCounts[a.id] || 0;
        conditions.push({ abbr: 'Prod', label: 'Product', value: this.pluralize(count, 'Product', 'Products') });
      }

      if (a.use_crm_campaign_filter) {
        const count = a.crm_campaigns ? a.crm_campaigns.length : 0;
        conditions.push({ abbr: 'Camp', label: 'Campaign', value: this.pluralize(count, 'Campaign', 'Campaigns') });
      }
    }
    return conditions;
  }

  getConditionBadges(offer: RetentionOffer): AudienceCondition[] {
    return this.getAudienceConditions(offer).slice(0, 3);
  }

  getConditionDetails(offer: RetentionOffer): AudienceCondition[] {
    return this.getAudienceConditions(offer);
  }

  isDropdownOpen(inputIndex: number): boolean {
    return this.openDropdownIndex === inputIndex;
  }

  isOfferUpdating(inputIndex: number): boolean {
    return this.offerLoading[inputIndex] || false;
  }

  onSearchOffer(inputIndex: number): void {
    const query = (this.searchQuery[inputIndex] || '').toLowerCase();
    this.filteredOffers = this.offers.filter((offer) => {
      // Search in offer properties
      const offerDisplayId = (offer.display_id && offer.display_id.toString()) || '';
      const offerName = (offer.name && offer.name.toLowerCase()) || '';

      // Search in audience properties (if audience exists)
      const audienceId = (offer.audience && offer.audience.id && offer.audience.id.toString()) || '';
      const audienceName = (offer.audience && offer.audience.name && offer.audience.name.toLowerCase()) || '';

      return (
        offerDisplayId.includes(query) ||
        offerName.includes(query) ||
        audienceId.includes(query) ||
        audienceName.includes(query)
      );
    });
  }

  addNewSegment(inputIndex: number): void {
    this.closeDropdown();
    this.currentSegmentInputIndex = inputIndex;

    const defaultOffer = this.offers.find((offer) => !offer.audience);

    if (defaultOffer) {
      this.segmentPopupTitle = 'Create New Segment';
      this.segmentPopupData = {
        offer_id: defaultOffer.id,
        popupType: AudiencePopup.Create,
        name: null,
        audience_id: null,
        is_survey_response: true,
      };

      this.ngxSmartModalService.getModal('segmentCrudPopup').open();
    }
  }

  onSegmentSave(data: any): void {
    if (data && data.name && data.offer_id && this.currentSegmentInputIndex !== null) {
      const defaultOffer = this.offers.find((offer) => !offer.audience);

      if (defaultOffer) {
        const inputs = this.form.get('inputs') as FormArray;
        const inputControl = inputs.at(this.currentSegmentInputIndex);
        const reasonCategoryControl = inputControl && inputControl.get('reason_category');
        const reasonCategory = reasonCategoryControl && reasonCategoryControl.value;

        if (reasonCategory) {
          this.closeSegmentCrudPopup();
          this.offerLoading[this.currentSegmentInputIndex] = true;

          // Get the previously selected offer
          const previousOfferKey = this.selectedOffers[this.currentSegmentInputIndex];
          const previousOffer = this.findOfferByKey(previousOfferKey);

          const audienceData = {
            name: data.name,
            parent_path: data.offer_id,
            reason_categories: [reasonCategory],
            use_reason_filter: true,
          };

          this.audienceService.create(audienceData).subscribe(
            (newAudience: any) => {
              const newOffer: RetentionOffer = {
                ...defaultOffer,
                audience: newAudience,
              };

              this.offers.push(newOffer);
              this.filteredOffers = [...this.offers];

              // Update previous audience if it had one
              if (previousOffer && previousOffer.audience && previousOffer.audience.reason_categories) {
                const previousCategories = previousOffer.audience.reason_categories.filter(
                  (cat) => cat !== reasonCategory
                );
                this.audienceService
                  .patch(previousOffer.audience.id, {
                    reason_categories: previousCategories,
                    use_reason_filter: previousCategories.length > 0 ? true : false,
                  })
                  .subscribe(
                    () => {
                      this.selectedOffers[this.currentSegmentInputIndex] = this.getOfferKey(newOffer);
                      this.offerLoading[this.currentSegmentInputIndex] = false;
                      this.currentSegmentInputIndex = null;
                      this.alertService.success('New segment added');
                    },
                    (error) => {
                      this.offerLoading[this.currentSegmentInputIndex] = false;
                      this.handleError(error);
                      this.currentSegmentInputIndex = null;
                    }
                  );
              } else {
                // No previous audience, just select the new offer
                this.selectedOffers[this.currentSegmentInputIndex] = this.getOfferKey(newOffer);
                this.offerLoading[this.currentSegmentInputIndex] = false;
                this.currentSegmentInputIndex = null;
                this.alertService.success('New segment added');
              }
            },
            (error) => {
              this.offerLoading[this.currentSegmentInputIndex] = false;
              this.handleError(error);
              this.currentSegmentInputIndex = null;
            }
          );
        }
      }
    }
  }

  closeSegmentCrudPopup(): void {
    this.ngxSmartModalService.getModal('segmentCrudPopup').close();
  }

  toggleThreeDotMenu(index: number): void {
    const isOpen = this.showThreeDotMenu[index];
    this.showThreeDotMenu = {};
    if (!isOpen) {
      this.showThreeDotMenu[index] = true;
    }
  }

  closeThreeDotMenu(index: number): void {
    this.showThreeDotMenu[index] = false;
  }

  fetchAudienceProducts(): void {
    this.offers.forEach((offer) => {
      if (offer.audience && offer.audience.use_product_filter) {
        this.campaignProductService
          .list({ audiences: offer.audience.id, page: 1, page_size: config.maxPageSize })
          .subscribe(
            (response: any) => {
              this.audienceProductCounts[offer.audience.id] = response.count || 0;
            },
            (error) => {
              this.handleError(error);
            }
          );
      }
    });
  }

  getAudienceConditionCount(offer: RetentionOffer): number {
    return this.getAudienceConditions(offer).length;
  }

  mapInputsToOffers(): void {
    const inputs = this.form.get('inputs') as FormArray;

    inputs.controls.forEach((inputControl, index) => {
      const reasonCategoryControl = inputControl.get('reason_category');
      const reasonCategory = reasonCategoryControl && reasonCategoryControl.value;

      if (reasonCategory) {
        // Priority 1: Find offer with matching reason_categories in audience
        let matchingOffer = this.offers.find((offer) => {
          if (offer.audience && offer.audience.reason_categories && Array.isArray(offer.audience.reason_categories)) {
            return offer.audience.reason_categories.includes(reasonCategory);
          }
          return false;
        });

        // Priority 2: If no category match, fallback to default offer (audience: null)
        if (!matchingOffer) {
          matchingOffer = this.offers.find((offer) => !offer.audience);
        }

        // Auto-select the matching offer
        if (matchingOffer) {
          this.selectedOffers[index] = this.getOfferKey(matchingOffer);
        }
      }
    });
  }
}
